import type { History } from 'history'

import type { FilterOption } from '@/app/component/atom/filters/types'
import type { SendWhatsappMessagePayload } from '@/app/module/logs/types'
import type { FileUploadType } from '@/app/types'
import type { DirectionType, WhatsAppLog } from '@/app/types/log'

export type LogTab = 'messages' | 'calls' | 'whatsapp' | 'topups'

export type LogFilters = {
  type?: string
  status?: string
  from?: string
  to?: string
}

export type LogQuery = {
  filters?: LogFilters
  pageSize?: number
  search?: string
  endCursor?: string
}

export type LogFilterOptions = Record<string, FilterOption>

export type LogNavigatePayload = {
  history: History
  tab: LogTab
  search: string
  filters: LogFilterOptions
}

export type LogRequestArgs = {
  token: string
  orgId: number
  query?: LogQuery
}

export type LogDownloadArgs = {
  token: string
  orgId: number
  query?: Pick<LogQuery, 'filters' | 'search'>
}

type LogContactInfo = {
  contactDeleted: boolean
  contactId: number
}

export type MessageLog = {
  campaignName?: string
  id: string
  direction: DirectionType
  created: string
  chargedMicro: number
  sender: string
  recipient: string
  message: string
  deliveredToTelco: boolean
  deliveredToPhone: string
  done: boolean
  engagementFlowId?: number
  pricePerPart?: number
  successPartCount?: number
}

export type CallLog = {
  campaignName?: string
  id: string
  direction: DirectionType
  created: string
  callerId: string
  to: string
  isMissedCall: boolean
  answer: string
  finalError: string
  finalErrorTime: string
  chargedMicro: number
  duration: number
  engagementFlowId?: number
}

export type TopupLog = {
  campaignId?: number
  campaignName?: string
  phoneNumber: number
  done: boolean
  success: boolean
  error: string
  priceCharged: string
  topupReceived: number
  topupRequested: number
  topupCurrency: string
  created: string
}

export type MessageLogItem = MessageLog & LogContactInfo
export type CallLogItem = CallLog & LogContactInfo
export type WhatsAppLogItem = WhatsAppLog & LogContactInfo
export type TopupLogItem = TopupLog & LogContactInfo

type LogDataByTab = {
  messages: MessageLogItem
  calls: CallLogItem
  whatsapp: WhatsAppLogItem
  topups: TopupLogItem
}

export type CallProps = CallLogItem & {
  isLoadingRow: boolean
  windowWidth: number
  viewContact: (contactId: number) => void
}

export type TabElement<T> = {
  downloadData: LogDownloadHandler
  getData: LogRequestHandler
  reloadData: LogVoidHandler
  data: T[]
}

export type TabMap = {
  [key in LogTab]: TabElement<LogDataByTab[key]>
}

type PersonalizationItem = {
  label: string
  value: string
  access: boolean
}

export type PersonalizationList = {
  contact: PersonalizationItem[]
  customfields: PersonalizationItem[]
}

type RecipientOption = {
  label: string
  value: string
}

type SendWhatsappMessageArgs = SendWhatsappMessagePayload & {
  token: string
  orgId: number
}

type LogRequestHandler = (args: LogRequestArgs) => Promise<unknown>
type LogDownloadHandler = (args: LogDownloadArgs) => Promise<unknown> | void
type LogVoidHandler = () => void

export type LogProps = {
  contactIds: Array<number | string>
  callFilters: LogFilterOptions
  calls: CallLogItem[]
  endCursor: string
  error: string
  from: string
  hasNextPage: boolean
  history: History
  messages: MessageLogItem[]
  messageFilters: LogFilterOptions
  msgSenderId: string
  orgId: number
  pageSize: number
  personalizationList: PersonalizationList
  recipients: RecipientOption[]
  search: string
  status: string
  tab: LogTab
  timezone: string
  token: string
  to: string
  topups: TopupLogItem[]
  type: string
  whatsapp: WhatsAppLogItem[]
  whatsappSenderIds: RecipientOption[]
  clearFilters: () => void
  downloadCalls: LogDownloadHandler
  downloadMessages: LogDownloadHandler
  downloadTopups: LogDownloadHandler
  downloadWhatsapp: LogDownloadHandler
  getFilesItem: (args: { orgId: number; token: string; name: string }) => Promise<unknown>
  getCustomFields: (args: { orgId: number; token: string }) => Promise<unknown>
  getCalls: LogRequestHandler
  getMessages: LogRequestHandler
  getTopups: LogRequestHandler
  getWhatsapp: LogRequestHandler
  navigateLogs: (payload: LogNavigatePayload) => void
  onMicAccessError: () => void
  reloadCalls: LogVoidHandler
  reloadMessages: LogVoidHandler
  reloadTopups: LogVoidHandler
  reloadWhatsapp: LogVoidHandler
  saveWhatsappFile: (args: { token: string; orgId: number; item: FileUploadType }) => Promise<unknown>
  sendWhatsappMessage: (args: SendWhatsappMessageArgs) => Promise<unknown>
}

const fmtr = new Intl.NumberFormat('us-us', {
  style: 'decimal',
  useGrouping: false,
  minimumFractionDigits: 3,
  maximumFractionDigits: 6,
})

export const formatCost = (micro: number): string => `$ ${fmtr.format(micro / 1000000)}`
